import type { DiscountInfo } from '@store/generated/subscriptions';

import type { BundleWithStatus, CapabilityGrant } from './types';

/**
 * Format a price in cents to a human-readable string.
 * e.g., 1999 -> "$19.99", 2000 -> "$20"
 */
export function formatPrice(cents: number, currency = 'usd'): string {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Get the monthly price amount in cents for a bundle, accounting for billing interval */
export function getMonthlyAmountCents(bundle: BundleWithStatus, isAnnual: boolean): number {
  return isAnnual ? Math.round(bundle.yearly_unit_amount / 12) : bundle.monthly_unit_amount;
}

/**
 * Calculate the discount amount in cents for a given subtotal.
 * Returns the amount to subtract (always positive or zero).
 *
 * For percentage discounts: applies percent_off to the subtotal.
 * For fixed discounts: amount_off is per-invoice, so for annual billing
 * we divide by 12 to get the monthly equivalent.
 */
export const calculateDiscountAmount = (
  discount: DiscountInfo | null | undefined,
  subtotalCents: number,
  billingInterval: string,
): number => {
  if (!discount) return 0;

  if (discount.percent_off) {
    return Math.round((subtotalCents * discount.percent_off) / 100);
  }

  if (discount.amount_off) {
    // amount_off is per-invoice; for annual billing show monthly equivalent
    if (billingInterval === 'year') {
      return Math.round(discount.amount_off / 12);
    }
    return discount.amount_off;
  }

  return 0;
};

/**
 * Return true when `tierGrant` covers everything `addonGrant` provides — i.e.,
 * the tier has the same capability at least as generously.
 *
 * - Tier unlimited (`quota == null`) covers any add-on quota
 * - Tier finite + add-on unlimited: not covered
 * - Both finite: covered iff tier.quota >= addon.quota
 */
const tierGrantCoversAddonGrant = (
  tierGrant: CapabilityGrant | undefined,
  addonGrant: CapabilityGrant,
): boolean => {
  if (!tierGrant) return false;
  if (tierGrant.quota == null) return true;
  if (addonGrant.quota == null) return false;
  return tierGrant.quota >= addonGrant.quota;
};

/**
 * Compute which add-on bundles are functionally "included" in a tier — i.e., the
 * tier's grants fully cover the add-on's grants, so owning the add-on on top of
 * the tier adds nothing.
 *
 * Used to render "+ JotRx included" lines on tier cards in the group overview.
 * Driven entirely by catalog data, so when the backend updates grants (e.g.,
 * adds JotMeet capability to Everything, or gates JotRx differently), the
 * frontend display follows automatically — no FE config changes needed.
 *
 * An add-on with no grants is never reported as included (defensive: we'd
 * rather hide a marketing line than over-promise).
 */
export const getAddOnsIncludedInTier = (
  tier: BundleWithStatus,
  addOns: BundleWithStatus[],
): BundleWithStatus[] => {
  const tierGrantsByCapability = new Map<string, CapabilityGrant>(
    (tier.grants || []).map(g => [g.capability_name, g]),
  );

  return addOns.filter(addon => {
    const addonGrants = addon.grants || [];
    if (addonGrants.length === 0) return false;
    return addonGrants.every(addonGrant =>
      tierGrantCoversAddonGrant(tierGrantsByCapability.get(addonGrant.capability_name), addonGrant),
    );
  });
};

/**
 * Get human-readable duration text for a Stripe discount.
 */
export const getDiscountDurationText = (
  discount: DiscountInfo | null | undefined,
): string | null => {
  if (!discount?.duration) return null;

  switch (discount.duration) {
    case 'once':
      return null;
    case 'repeating':
      return discount.duration_in_months
        ? `Applies for ${discount.duration_in_months} months`
        : 'Applies for multiple billing cycles';
    case 'forever':
      return 'Ongoing discount';
    default:
      return null;
  }
};
